<template>
  <div
    class="biBarWrap biLineWrap"
    ref="biLineWrap">
    <svg
      v-if="data && data.length"
      ref="svg"
      :width="clientWidth"
      :height="clientHeight"
      :class="{'biChartBlur': refresh !== 0}">
      <bi-label
        :data="classes.data"
        :options="classes.options"
        @classesClick="classesClick"/>
      <g
        class="area"
        ref="area">
        <g
          class='scroll'
          ref="scroll">
          <rect
            class="hoverRect"
            fill="#F3F3F3"
            opacity="0"
            :x="left"
            :y="top"
            :height="innerHeight"/>
          <g
            class="rect"
            ref="rect"/>
          <g
            class="point"
            ref="point"/>
          <g
            class="linear"
            ref="linear"/>
          <g
            class="axisX"
            ref="axisX"/>
        </g>
        <g
          class="axisY"
          ref="axisY"/>
        <g
          class="axisY1"
          ref="axisY1"/>
      </g>
      <g
        class="focusBar"
        ref="focusBar"
        opacity="0">
        <rect
          :x="left"
          :y="top"
          :width="innerWidth"
          :height="innerHeight"
          @mouseover="opacityFocus(1)"
          @mouseout="opacityFocus(0)"
          @mousemove="inventFocus"/>
        <circle
          v-if="classes.data.length"
          :cx="focus.focusX"
          :cy="focus.focusY"
          :fill="classes.data[classes.data.length - 1].color"
          r="4"/>
      </g>
      <defs>
        <clipPath :id="hashId">
          <rect
            :width="innerWidth"
            :height="innerHeight + 1"
            :x="left"
            :y="top - 1"/>
        </clipPath>
      </defs>
    </svg>
    <div
      class="center-p"
      v-show="refresh === 0 && !(classes.data.length) || refresh === 1 || refresh === 2 || refresh === 3">
      <el-tag type="warning"><i
        class="el-icon-loading"
        v-show="refresh === 1"/>{{ message[refresh] }}</el-tag>
      <el-button
        type="primary"
        size="mini"
        @click="reload"
        v-show="refresh === 0">
        <i class="el-icon-refresh"/>点击刷新
      </el-button>
    </div>
    <bi-tool-tip
      class="tooltip"
      :data="tooltip"/>
  </div>
</template>

<script>
import * as d3 from 'd3';
import Common from '@/components/charts/common';

export default {
  mixins: [Common],
  data() {
    return {
      hashId: '',
      defaultOptions: {
        width: 0,
        height: 0,
        margin: {
          top: 50, right: 24, bottom: 20, left: 40
        },
        domainType: 'number',
        domainX: null,
        domainY: null,
        curve: '',
        groupType: true,
        exLabel: '%%',
        duration: 400,
        labelWidthPercent: 25
      },
      scrollX: 0,
      bar: {
        defaultBarWidth: 30,
        barStep: 0,
      },
      init: false
    };
  },
  props: {
  },
  computed: {
    scaleX() {
      let scale;
      if (this.option.domainX) {
        switch (this.option.domainType) {
          case 'time':
            scale = d3.scaleTime().range([this.left, this.dataWidth + this.left]);
            break;
          case 'string':
            scale = d3.scaleBand().range([this.left, this.dataWidth + this.left])
              .domain(this.option.domainX);
            break;
          default:
            scale = d3.scaleLinear().range([this.left, this.dataWidth + this.left])
              .domain(d3.extent(this.option.domainX, d => d));
        }
      }
      return scale;
    },
    scaleY1() {
      return d3.scaleLinear()
        .range([this.bottom, this.top])
        .domain(d3.extent(this.option.domainY1, d => d));
    },
    line1() {
      let lines = d3.line();
      lines
        .x(d => this.scaleX(d.label) + (this.bar.barStep / 2))
        .y(this.bottom);
      return lines;
    },
    line() {
      let lines = d3.line();
      lines
        .x(d => this.scaleX(d.label) + (this.bar.barStep / 2))
        .y(d => this.scaleY1(d.value));
      return lines;
    },
  },
  methods: {
    createSvg() {
      if (this.data.length === 0) return;
      this.initData();
    },
    createAllAttr() {
      d3.select(this.$refs.axisX).selectAll('*').remove();
      d3.select(this.$refs.axisY).selectAll('*').remove();
      d3.select(this.$refs.linear).selectAll('*').remove();
      // 画轴
      // this.scaleX.domain(this.option.domainX);
      let classes = this.classes.data;
      // 这里只计算分组柱，叠柱形式还未计算
      let values = this.data.map((d) => {
        if (this.getLabelStatus(d.class, classes)) {
          return d.value;
        }
        return 0;
      });
      this.scaleY.domain([0, d3.max(values, d => d) || 1]);
      d3.select(this.$refs.axisX)
        .attr('transform', `translate(0, ${this.bottom})`)
        .call(d3.axisBottom(this.scaleX));
      const tick = (d3.max(values, d => d) / 5) | 0;
      d3.select(this.$refs.axisY)
        .attr('transform', `translate(${this.left}, 0)`)
        .call(d3.axisLeft(this.scaleY).ticks(tick > 2 ? 5 : d3.max(values, d => d)));
      d3.select(this.$refs.axisY1)
        .attr('transform', `translate(${this.right}, 0)`)
        .call(d3.axisRight(this.scaleY1).ticks(10).tickFormat(d => d * 100))
        .append('text')
        .attr('fill', '#000')
        .attr('y', this.top)
        .attr('dx', -10)
        .attr('dy', 5)
        .attr('text-anchor', 'end')
        .text('单位 %');
      const domainX = this.option.domainX;
      this.bar.barStep = this.dataWidth / domainX.length; // 实际刻度的长度
    },
    // 每一个rect是通过左上角坐标定位的
    initData() {
      if (this.option.domainX && this.option.domainY) {
        this.createAllAttr();
      } else {
        // 输出一个无数据的效果
        return null;
      }
      // this.svg = d3.select(this.$refs.rect);
      // let i = 0;
      let point = d3.select(this.$refs.point);
      let line = d3.select(this.$refs.linear);
      point.attr('clip-path', `url(#${this.hashId})`);
      line.attr('clip-path', `url(#${this.hashId})`);
      this.update1 = line.selectAll('path').data(this.data1).remove();
      this.update2 = point.selectAll('circle').data(this.data).remove();
      let enter1 = this.update1.enter();
      let enter2 = this.update2.enter();

      enter1
        .append('path')
        .datum(d => d.list)
        .attr('fill', 'none')
        .attr('stroke', (d, i) => this.getColor(this.data1[i].title, this.classes.data))
        .attr('stroke-width', 1.5)
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round')
        .attr('stroke-dasharray', '3, 3')
        .attr('d', this.line1)
        .transition()
        .duration(this.duration)
        .attr('d', this.line)
        .attr('opacity', (d, i) => this.getLabelStatus(this.data1[i].title, this.classes.data))
        .ease(d3.easeElastic);
      enter2
        .append('circle')
        .attr('fill', d => this.getColor(d.class, this.classes.data))
        .attr('r', 8)
        .attr('cx', d => this.scaleX(d.label) + (this.bar.barStep / 2))
        .attr('cy', d => this.scaleY(d.value));
      let drag = d3.drag();
      let focusBar = d3.select(this.$refs.focusBar);
      focusBar
        .call(drag
          .on('drag', () => {
            let point = d3.select(this.$refs.scroll).select('.point');
            let line = d3.select(this.$refs.scroll).select('.linear');
            let xAxis = d3.select(this.$refs.scroll).select('.axisX');
            let scrollX = this.scrollX + d3.event.dx;
            this.scrollX = Math.min(0, scrollX);
            this.scrollX = Math.max(-(this.dataWidth - this.innerWidth), this.scrollX);
            line
              .selectAll('path')
              .attr('transform', `translate(${this.scrollX}, 0)`);
            point
              .selectAll('rect')
              .attr('transform', `translate(${this.scrollX}, 0)`);
            xAxis
              .attr('transform', `translate(${this.scrollX}, ${this.bottom})`);
          }));
    },
    opacityFocus(opacity) {
      d3
        .select(this.$refs.focusBar)
        .transition()
        .duration(this.duration / 2)
        .attr('opacity', opacity || 0);
      d3
        .select(this.$refs.biLineWrap)
        .select('.tooltip')
        .transition()
        .duration(this.duration / 2)
        .style('opacity', opacity || 0);
      d3
        .select(this.$refs.scroll)
        .select('.hoverRect')
        .attr('width', this.scaleX.bandwidth())
        .attr('opacity', opacity || 0);
    },
    inventFocus(e) {
      if (this.data.length === 0) return;
      let scaleX = this.scaleX;
      let scaleY = this.scaleY1;
      let mouseX = e.layerX - this.left;
      let index = ((mouseX - this.scrollX) / this.bar.barStep);
      let x1 = this.option.domainX[index | 0];
      this.focus.focusX = scaleX(x1) + (this.bar.barStep / 2) + this.scrollX;
      this.focus.focusY = scaleY(this.data1[0].list[index | 0].value);
      x1 && d3
        .select(this.$refs.scroll)
        .select('.hoverRect')
        .transition()
        .duration(this.duration / 3)
        .attr('x', scaleX(x1) + this.scrollX);
      this.tooltip = {
        panelWidth: this.innerWidth,
        desc: [],
      };
      this.data.forEach((d) => {
        if (d.label === x1) {
          let label = this.getLabel(d.class, this.classes.data);
          if (label.status && d.value) {
            label.value = d.value;
            this.tooltip.desc.push(label);
          }
        }
      });
      this.data1.forEach((d) => {
        let label = this.getLabel(d.title, this.classes.data);
        if (label.status) {
          label.value = d3.sum(d.list.map((d) => {
            if (d.label === x1) {
              return d.value;
            }
            return 0;
          }));
          if (this.option.exY1 && this.option.exY1 === '%') {
            label.value = `${(label.value * 100).toFixed(2)}%`;
          }
          if (label.value !== 0) {
            this.tooltip.desc.push(label);
          }
        }
      });
      this.tooltip.x = e.layerX;
      this.tooltip.y = e.layerY;
      this.tooltip.title = this.option.exLabel.replace(/%%/, x1);
    },
    classesClick(ix) {
      this.classes = Object.assign({}, this.classes, {
        data: this.changeClassStatus(ix, this.classes.data)
      });
      this.initData();
    },
    getRealIndex(text, classes) {
      let index = 0;
      if (classes) {
        for (let i = 0; i < classes.length; i++) {
          if (classes[i].title === text) {
            break;
          }
          if (classes[i].status) {
            index++;
          }
        }
      }
      return index;
    }
  },
  watch: {
    data() {
      this.$nextTick(() => {
        this.scrollX = 0;
        if (this.option.classes) {
          this.classes.data = this.option.classes.map((d, i) => {
            return {
              title: d,
              color: this.colors[i],
              type: 'bar',
              status: true,
              view: true
            };
          });
          this.classes.options = {
            panelWidth: this.clientWidth,
            labelWidthPercent: this.option.labelWidthPercent
          };
          if (this.option.classes1) {
            let classes1 = this.option.classes1;
            for (let i = classes1.length - 1; i >= 0; i--) {
              let index = (i + this.option.classes.length) % this.colors.length;
              this.classes.data.push({
                title: classes1[i],
                color: this.colors[index],
                type: 'line',
                status: true,
                view: true
              });
            }
          }
        }
        if (!this.init) {
          this.createSvg();
          this.init = true;
        } else {
          this.initData();
        }
      });
    }
  }
};
</script>

<style>
.biBarWrap {
  position: relative;
  height: 100%;
}

.biChartBlur {
  opacity: .25;
  filter: blur(12px);
}

.biBarWrap svg {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.biBarWrap .axis path,
.biBarWrap .axis line {
  fill: none;
  stroke: black;
  shape-rendering: crispEdges;
}

.biBarWrap .center-p {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.biBarWrap .center-p .state {
  position: relative;
  z-index: 100;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.biBarWrap .center-p button {
  margin-top: 20px;
}

.biBarWrap .focusBar rect {
  fill: none;
  pointer-events: all;
}

.biBarWrap .focusBar circle {
  stroke: #333;
  stroke-width: 0;
  pointer-events: none;
}

</style>
